import React from "react";
import { message as antdMessage } from "antd";

const MOBILE_BREAKPOINT = 767;
const DEFAULT_DURATION = 3;
const GENERIC_TITLES = ["success", "error", "warning", "info"];

const isMobileView = () =>
  typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT;

const isRenderable = (value) =>
  value !== undefined && value !== null && value !== "";

const stringifyContent = (value) => {
  if (!isRenderable(value)) return "";
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) {
    return value.filter(isRenderable).join("\n");
  }
  if (typeof value === "object") {
    return value.message || value.description || value.content || "";
  }
  return value;
};

const buildContent = (options) => {
  if (!options || React.isValidElement(options) || typeof options !== "object") {
    return stringifyContent(options);
  }

  const title = stringifyContent(options.message || options.content);
  const description = stringifyContent(options.description);
  const normalizedTitle = typeof title === "string" ? title.trim().toLowerCase() : "";

  if (isRenderable(description) && GENERIC_TITLES.includes(normalizedTitle)) {
    return description;
  }

  if (isRenderable(description)) {
    return isRenderable(title) ? `${title}: ${description}` : description;
  }

  return title;
};

const normalizeOptions = (type, options, duration, onClose) => {
  const config = options && typeof options === "object" && !React.isValidElement(options)
    ? { ...options }
    : {};
  const content = buildContent(options);

  delete config.message;
  delete config.description;

  return {
    duration: duration ?? config.duration ?? DEFAULT_DURATION,
    onClose: onClose ?? config.onClose,
    ...config,
    type,
    content: (
      <span className="global-message-content">
        {content}
      </span>
    ),
    className: ["global-message", config.className].filter(Boolean).join(" "),
    style: {
      maxWidth: isMobileView() ? "calc(100vw - 32px)" : 568,
      margin: "0 auto",
      ...config.style,
    },
  };
};

const open = (type, options, duration, onClose) => {
  const config = normalizeOptions(type, options, duration, onClose);
  if (!isRenderable(config.content?.props?.children)) return undefined;

  antdMessage.config({
    top: isMobileView() ? 64 : 24,
    duration: DEFAULT_DURATION,
    maxCount: 3,
  });

  return antdMessage.open(config);
};

const globalMessage = {
  open: (options) => open(options?.type || "info", options),
  success: (options, duration, onClose) => open("success", options, duration, onClose),
  error: (options, duration, onClose) => open("error", options, duration, onClose),
  warning: (options, duration, onClose) => open("warning", options, duration, onClose),
  warn: (options, duration, onClose) => open("warning", options, duration, onClose),
  info: (options, duration, onClose) => open("info", options, duration, onClose),
  loading: (options, duration, onClose) => open("loading", options, duration, onClose),
  destroy: (key) => antdMessage.destroy(key),
  config: (options) => antdMessage.config(options),
};

export default globalMessage;
